import { I18n } from '../models/i18n'
import {
    Portal,
    // SmartlyActionItems,
    // SmartlyActionTypes,
    UserPermissions,
} from '../models/permissions'
import { User } from '../models/user'
import { getActions } from '../redux/actions/permissions'
import {
    getEnabledApps,
    loggedIn,
    logout,
    setPortal,
    switchLocale,
    getProfileInfo,
} from '../redux/actions/user'
import { AppState } from '../redux/states'
import configureStore from '../redux/store'
import { ability, digitalportalURL } from '../Techedge-digitalportal-shared'
import history from './history'
import { LocalStorage } from './localstorage'
import { UserAbilityHelper } from './userAbilityHelper'
// import { FiltersApi } from '../api/filtersApi'
// import { Filter } from '../models/filters'
// import { setFiltersList, setActiveFilter } from '../redux/actions/filters'

export enum UserHelperEvents {
    DATA_CHANGED = 'DATA_CHANGED',
    LOGOUT = 'LOGOUT',
    LOGOUT_ERROR = 'LOGOUT_ERROR',
    PERMISSION_UPDATED = 'PERMISSION_UPDATED',
}

/**
 *  The class is a singleton service that rules upon all related actions and data concerning the authenticated user
 * @export
 * @class UserHelper
 */
export class UserHelper {
    /**
     * Instance of the singleton
     * @private
     * @static
     * @type {UserHelper}
     * @memberof UserHelper
     */
    private static shared: UserHelper

    /**
     * Redux store object to keep tracks of all the user related data
     * @private
     * @type {*}
     * @memberof UserHelper
     */
    public store: any

    public userAbilityHelper: UserAbilityHelper

    private constructor() {}

    public static _shared(): UserHelper {
        if (!UserHelper.shared) {
            UserHelper.shared = new UserHelper()
            UserHelper.shared.userAbilityHelper = new UserAbilityHelper(ability)
            UserHelper.shared.store = configureStore()

            UserHelper.shared.store.subscribe(() => {
                // let userState: UserState = UserHelper._shared()._state()
                //     .userReducer
                // console.log("STORE UPDATED", userState);
                // let event: Event = new Event(UserHelperEvents.DATA_CHANGED);
                let event = window.document.createEvent('UIEvents')
                event.initEvent(UserHelperEvents.DATA_CHANGED, true, false)
                window.dispatchEvent(event)
            })
        }

        return UserHelper.shared
    }

    /**
     * User state from redux store
     * @private
     * @memberof UserHelper
     */
    private _state = (): AppState => {
        return UserHelper._shared().store.getState() as AppState
    }

    /**
     * Dispatch the action passed as a parameter
     * @param action - The redux action that needs to be dispatched
     * @private
     * @memberof UserHelper
     */
    private _dispatch = (action: any): any => {
        return UserHelper._shared().store.dispatch(action)
    }

    /**
     * Get the access token from the redux state. The token is mandatory for api authentication
     * @returns {string} Returns the access token string
     */
    public accessToken = (): string => {
        return UserHelper._shared()._state().userReducer.accessToken
    }
    /**
     * Get the access token from the redux state. The token is mandatory for api authentication
     * @returns {string} Returns the access token string
     */
    public appPermissions = (): any => {
        return UserHelper._shared()._state().userReducer.permissions
    }

    /**
     * Get the refresh token from the redux state. The token is mandatory to get a new valid access token
     * @returns {string} Returns the refresh token string
     */
    public refreshToken = (): string => {
        return UserHelper._shared()._state().userReducer.refreshToken
    }

    /**
     * Get the locale from the redux state.
     * @returns {string} Returns the locale string
     */
    public i18n = (): I18n => {
        return UserHelper._shared()._state().userReducer.i18n
    }

    /**
     * Get the user data from the redux state
     * @returns {User} Returns the user data as a User
     */
    public user = (): User => {
        return UserHelper._shared()._state().userReducer.user
    }

    public permissions = (): UserPermissions => {
        return (
            UserHelper._shared()._state().userReducer.permissions ||
            new UserPermissions()
        )
    }

    public setPortal = (code: string): void => {
        LocalStorage._shared().clearSelectedPortal()
        UserHelper._shared()._dispatch(setPortal(code))
    }

    public getProfileInfo = (): void => {
        UserHelper._shared()._dispatch(getProfileInfo())
    }

    goToDefaultApp = (permissions?: UserPermissions) => {
        const _permissions = permissions
            ? permissions
            : UserHelper._shared().permissions()

        if (window.location.hostname.includes('smartly')) {
            return
        }

        // remove jwt from digitalportal url
        if (
            window.location.pathname.includes('task') &&
            window.location.search.includes('jwt')
        ) {
            window.location.search = ''
            return
        }
        if (window.location.origin == digitalportalURL) history.push('/')
        // navigate to default app
        else if (_permissions.portals) {
            let selectedPortal = UserHelper._shared().getPortalDetails(
                _permissions.portals
            )
            history.push(
                selectedPortal?.default_path
                    ? selectedPortal.default_path
                    : selectedPortal?.applications[0]?.path
            )
        }
    }

    public getPortalDetails = (portals: Portal[], code?: string): any => {
        try {
            if (portals?.length) {
                if (portals.length == 1) return portals[0]
                else if (code) return portals.find((p) => p.code === code)
                else {
                    const selectedPortal = LocalStorage._shared()._get(
                        LocalStorage.SELECTED_PORTAL_CODE
                    )
                    return portals.find((p) => p.code == selectedPortal)
                }
            }
            return new Portal()
        } catch (error) {
            return undefined
        }
    }

    //REDUX ACTIONS

    /**
     * Dispatch the loggedIn function, a redux action that needs to be triggered after a login occurred
     * @param {string} accessToken
     * @param {string} refreshToken
     */
    public doLogin = (
        accessToken: string,
        refreshToken: string,
        mode?: any
    ): void => {
        UserHelper._shared()._dispatch(
            loggedIn(accessToken, refreshToken, mode)
        )
    }
    /**
     * Dispatch the logout function, a redux action that needs to be triggered after the app has completed a navigation to the /logout path
     * @param {string} accessToken
     * @param {string} refreshToken
     */
    public doLogout = (): void => {
        UserHelper._shared()._dispatch(logout())
    }

    /**
     * Dispatch the switchLocale function, a redux action that needs to be triggered after a language change
     * @param {string} locale
     */
    public switchLocale = (locale: string): void => {
        UserHelper._shared()._dispatch(switchLocale(locale))
    }

    /**
     * Dispatch the getPermissions function, a redux action that needs to be triggered after the login process has completed its flow
     * @memberof UserHelper
     */
    public getAppPermissions = () => {
        let user: User
        if (window.location.hostname.includes('smartly')) {
            user = UserHelper._shared().user()
        }
        UserHelper._shared()._dispatch(getEnabledApps(false, user?.user_id))
    }

    /**********************************SMARTLY PERMISSIONS**********************************/

    public getSmartlyPermissions = (user: any) => {
        if (user) {
            UserHelper._shared()._dispatch(getActions(user))
        } else {
            console.debug('---- PERMISSIONS ERROR ------------------')
            console.debug('UserHelper getSmartlyPermissions', `user: ${user}`)
        }
    }
}
